import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import ColorInput from '../../UserInterfaceDesigner/Components/Inputs/ColorInput';
import DisplayContentTemplate from '../Components/DisplayContentTemplate';
import useFlowActivityMetadata from '../Hooks/Metadata/useFlowActivityMetadata';
import { ActivityModel } from '../Models/FlowModel';
import { ActivityNode } from '../Models/GraphModel';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import { GenericProperties } from './GenericProperties';
import AgentToolRoleChip from '../Components/AgentToolRoleChip';
import { ActivityCategories } from '../Models/ActivityConstants';
import { AgentPickerInput, ParameterTypes } from '@@common';

type ActivityPropertiesProps = {
	activity: ActivityNode;
	onActivityDataChanged?: (id: string, model: ActivityModel) => void;
	variableScope?: VariableScope;
	isVariableScopeLoading?: boolean;
	readOnly?: boolean;
	isAgentTool?: boolean;
	agentToolInputOverrides?: Record<string, boolean | undefined>;
	agentToolBoundInputs?: string[];
	agentToolInputDescriptions?: Record<string, string>;
	onAgentToolInputOverrideChanged?: (propertyName: string, enabled: boolean | undefined) => void;
	onAgentToolInputDescriptionChanged?: (propertyName: string, description: string | undefined) => void;
	onResetAgentToolInputOverrides?: () => void;
};

export function ActivityProperties({
	 activity,
	onActivityDataChanged,
	variableScope,
	isVariableScopeLoading,
	readOnly,
	isAgentTool = false,
	agentToolInputOverrides,
	agentToolBoundInputs,
	agentToolInputDescriptions,
	onAgentToolInputOverrideChanged,
	onAgentToolInputDescriptionChanged,
	onResetAgentToolInputOverrides,
}: ActivityPropertiesProps) {
	const activityMetadata = useFlowActivityMetadata(activity.data.Type);
	const isAgentService = activityMetadata?.categories?.includes(ActivityCategories.AGENT_SERVICE) === true;
	return (
		<>
			{isAgentService && (
				<Box sx={{ px: 2, pt: 1 }}>
					<FormControlLabel
						title="Show only the activity icon on the flow canvas"
						control={
							<Switch
								size="small"
								checked={activity.data.Metadata?.compact === true}
								disabled={readOnly}
								onChange={(_, compact) => onActivityDataChanged?.(activity.id, {
									...activity.data,
									Metadata: { ...activity.data.Metadata, compact },
								})}
							/>
						}
						label="Compact"
					/>
				</Box>
			)}
			<GenericProperties
				id={activity.data.Id}
				name={
					<Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
						<DisplayContentTemplate
							template={activityMetadata?.displayContent}
							fallbackText={activityMetadata?.displayName ?? activity.data.Type}
							context={activity.data}
							className=""
						/>
						{isAgentTool && <Box sx={{ ml: 'auto', display: 'flex' }}><AgentToolRoleChip role="activity" /></Box>}
					</Stack>
				}
				parameters={activityMetadata?.parameters ?? []}
				isAgentTool={isAgentTool}
				agentToolInputOverrides={agentToolInputOverrides}
				agentToolBoundInputs={agentToolBoundInputs}
				agentToolInputDescriptions={agentToolInputDescriptions}
				onAgentToolInputOverrideChanged={onAgentToolInputOverrideChanged}
				onAgentToolInputDescriptionChanged={onAgentToolInputDescriptionChanged}
				onResetAgentToolInputOverrides={onResetAgentToolInputOverrides}
				model={activity.data}
				onModelChanged={(model) => onActivityDataChanged?.(activity.id, model as ActivityModel)}
				variableScope={variableScope}
				isVariableScopeLoading={isVariableScopeLoading}
				readOnly={readOnly}
			>
				{(parameterMetadata, value, onValueChange) => {
					if (!readOnly && parameterMetadata.typeCustom === ParameterTypes.Color) {
						return <ColorInput color={value} onColorChange={onValueChange} />;
					}
					if (parameterMetadata.typeCustom === ParameterTypes.AgentId) {
						return <AgentPickerInput value={value} onValueChange={onValueChange} expression={parameterMetadata.type === 'Expression'} readOnly={readOnly} />;
					}
					return undefined;
				}}
			</GenericProperties>
		</>
	);
}
